import { ClearIcon, ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from '@assets/icons';
import classnames from 'classnames';
import React from 'react';

export enum AlertType {
    Info,
    Error,
    Warn,
    Success,
}

export type AlertAlign = 'start' | 'center' | 'end';

interface Props {
    type: AlertType;
    action?: () => void;
    actionText?: string;
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    classNames?: string;
    theme?: 'light' | 'dark';
    children?: React.ReactNode;
    fullWidth?: boolean;
    onClose?: () => void;
    align?: AlertAlign;
    solid?: boolean;
    fullBleed?: boolean;
    marginTop?: number | string;
    sticky?: boolean;
    showIcon?: boolean;
}

const getIcon = (type: AlertType) => {
    switch (type) {
        case AlertType.Error:
            return (
                <ErrorIcon
                    alt="Error"
                    style={{ width: 24, height: 24 }}
                />
            );
        case AlertType.Info:
            return (
                <InfoIcon
                    alt="Info"
                    style={{ width: 24, height: 24 }}
                />
            );
        case AlertType.Success:
            return <SuccessIcon alt="Success" />;
        case AlertType.Warn:
            return <WarningIcon alt="Warning" />;
        default:
            return null;
    }
};

const Alert: React.FunctionComponent<Props> = ({
    type,
    action,
    actionText,
    title,
    subtitle,
    classNames,
    theme,
    children,
    fullWidth = true,
    onClose,
    align = 'start',
    solid = false,
    fullBleed = false,
    marginTop,
    sticky = false,
    showIcon = true,
}) => {
    const icon = getIcon(type);
    return (
        <>
            <style jsx>{`
                @use 'vars' as *;
                @use 'utils' as *;
                @use 'mixins' as *;

                .Alert {
                    position: relative;
                    border-radius: $border-radius;
                    margin-bottom: grid(4);
                    display: flex;
                    align-items: center;
                    padding: grid(3);
                    line-height: grid(7);
                    background: $color-white;
                    border: 1px solid $color-subtitle;
                    color: $color-copy;

                    &--full {
                        display: flex;
                    }

                    &--fit {
                        display: inline-flex; /* shrink to content */
                        width: auto;
                    }

                    &--align-start {
                        justify-content: flex-start;
                    }

                    &--align-center {
                        justify-content: center;
                    }

                    &--align-end {
                        justify-content: flex-end;
                    }

                    &--has-close {
                        padding-right: grid(10);
                    }

                    &--solid {
                        border: none;

                        &.success {
                            background: rgba($color-success, 0.18);
                        }

                        &.warning {
                            background: rgba($color-warn, 0.18);
                        }

                        &.error {
                            background: rgba($color-error, 0.18);
                        }

                        &.info {
                            background: $info-secondary;
                        }
                    }

                    &--fullBleed {
                        margin-left: calc(50% - 50vw);
                        margin-right: calc(50% - 50vw);
                        border-radius: 0;
                    }

                    &--sticky {
                        position: sticky;
                        top: grid(13);
                        z-index: 10;
                    }

                    &--i-m {
                        margin-right: grid(2);

                        width: 24px; /* Explicitly set the width of the icon container */
                        flex-shrink: 0; /* Prevent it from shrinking */
                        margin-right: grid(2);
                        display: flex; /* Added to vertically center the icon if needed */
                        align-items: center; /* Added to vertically center the icon if needed */
                        justify-content: center;
                    }

                    p {
                        color: $color-copy;
                    }

                    > .link {
                        margin-left: grid(3);
                    }

                    .Alert--close {
                        position: absolute;
                        top: 50%;
                        right: grid(3);
                        transform: translateY(-50%);
                        background: none;
                        border: none;
                        padding: 0;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;
                        color: $color-copy;

                        :global(svg) {
                            width: 20px;
                            height: 20px;
                        }
                    }

                    &--fullBleed .Alert--close {
                        @media (min-width: $container-maxwidth) {
                            right: calc((100vw - #{$container-maxwidth}) / 2 + #{grid(3)});
                        }
                    }

                    &--Dark {
                        background: $color-secondary;
                        color: $color-white;
                        border: 0;

                        p {
                            color: $color-white;
                        }
                    }

                    &.success {
                        border-color: $color-success;

                        h3,
                        .Alert--i-m,
                        .Alert--close {
                            color: $color-success;
                        }
                    }

                    &.warning {
                        border-color: $color-warn;

                        h3,
                        .Alert--i-m,
                        .Alert--close {
                            color: $color-warn;
                        }
                    }

                    &.error {
                        border-color: $color-error;

                        h3,
                        .Alert--i-m,
                        .Alert--close {
                            color: $color-error;
                        }
                    }

                    &.info {
                        border-color: $info-primary;
                        background-color: $info-secondary;

                        @media (prefers-color-scheme: dark) {
                            background: $color-black;
                            color: $color-white;

                            .small {
                                color: $color-white;
                            }
                        }

                        h3,
                        .Alert--i-m,
                        .Alert--close {
                            color: $info-primary;
                        }
                    }

                    @media (prefers-color-scheme: dark) {
                        background: $color-black;
                        color: $color-white;

                        .small {
                            color: $color-white;
                        }
                    }

                    p {
                        margin: 0;
                    }
                }
            `}</style>
            <div
                className={classnames('Alert', classNames, {
                    error: type === AlertType.Error,
                    warning: type === AlertType.Warn,
                    info: type === AlertType.Info,
                    success: type === AlertType.Success,
                    'Alert--Dark': theme === 'dark',
                    'Alert--full': fullWidth,
                    'Alert--fit': !fullWidth,
                    'Alert--align-start': align === 'start',
                    'Alert--align-center': align === 'center',
                    'Alert--align-end': align === 'end',
                    'Alert--has-close': !!onClose,
                    'Alert--solid': solid,
                    'Alert--fullBleed': fullBleed,
                    'Alert--sticky': sticky,
                })}
                style={marginTop !== undefined ? { marginTop } : undefined}
            >
                {showIcon && <div className={'Alert--i-m'}>{icon}</div>}
                <div>
                    {title && <h3 className={subtitle ? 'm-b-8' : ''}>{title}</h3>}
                    {subtitle && <p className="small">{subtitle}</p>}
                    {children}
                </div>
                {action && actionText ? (
                    <div
                        className={'link'}
                        onClick={action}
                    >
                        {actionText}
                    </div>
                ) : null}
                {onClose ? (
                    <button
                        type="button"
                        className="Alert--close"
                        aria-label="Close"
                        onClick={onClose}
                    >
                        <ClearIcon aria-hidden />
                    </button>
                ) : null}
            </div>
        </>
    );
};
export default Alert;
